import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosError, AxiosResponse } from 'axios';
import apiClient from '../main/apiClient';
import Problem from "../types/problem";

/**
 * Hook for setting a new password using a reset token
 * @param pwdResetToken - The password reset token received via email
 * @returns Mutation object for setting new password
 * @throws AxiosError with Problem details if the request fails
 */
const useSetPassword = (pwdResetToken: string) => {
  return useMutation<AxiosResponse, AxiosError<Problem>, string>({
    mutationFn: (password) => 
      apiClient.post(`/password`, { 
        newPassword: password,
        passwordResetToken: pwdResetToken
      })
  });
};

/**
 * Hook for validating a password reset token. Retries on 400 responses (invalid token) are disabled.
 * @param pwdResetToken - The password reset token to validate
 * @returns Query object containing token validation status
 * @throws AxiosError with Problem details if the request fails
 */
const useCheckPasswordToken = (pwdResetToken: string) => {
  return useQuery<boolean, AxiosError<Problem>>({
    queryKey: ["checkPasswordToken", pwdResetToken],
    queryFn: () => 
      apiClient.get(`/password?passwordToken=${pwdResetToken}`), retry: (_failureCount, error: AxiosError) => error.response?.status !== 400
  });
}

/**
 * Hook for sending password reset email
 * @returns Mutation object for sending password reset email
 * @throws AxiosError with Problem details if the request fails
 */
const useSentResetPasswordEmail = () => {
  return useMutation<AxiosResponse, AxiosError<Problem>, string>({
    mutationFn: (emailAddress) => 
      apiClient.post(`/forgot-password`, { emailAddress })
  });
}

export { useSetPassword, useCheckPasswordToken, useSentResetPasswordEmail };